import React from "react";
import Select from "react-select";

/**
 * CommonSelect - Premium rounded pill react-select dropdown.
 *
 * Props:
 *   label      {string}   Label shown above the select
 *   required   {boolean}  Appends a red asterisk to the label
 *   options    {array}    Array of { value, label } options
 *   value      {object}   Currently selected option object
 *   onChange   {function} Called with the selected option object
 *   placeholder {string}
 *   isClearable{boolean}
 *   isDisabled {boolean}
 *   height     {number|string} Height of the select (default: 45)
 *   error      {string}   Error message to display below
 *   labelClass {string}   Extra classes for the label
 *   wrapClass  {string}   Extra classes for the wrapping div
 */
const CommonSelect = ({
  label,
  required = false,
  options = [],
  value,
  onChange,
  placeholder = "",
  isClearable = false,
  isDisabled = false,
  height = 45,
  error,
  labelClass = "",
  wrapClass = "",
}) => {
  const customStyles = {
    control: (base, state) => ({
      ...base,
      height: height,
      minHeight: height,
      borderRadius: "0.75rem",
      padding: "0 8px",
      border: error
        ? "1px solid #ff5e5e"
        : state.isFocused
          ? "1px solid #d5dfe7"
          : "1px solid #d5dfe7",
      boxShadow: "none",
      backgroundColor: isDisabled ? "#f8f9fa" : "#fff",
      "&:hover": {
        border: error
          ? "1px solid #ff5e5e"
          : state.isFocused
            ? "1px solid #d5dfe7"
            : "1px solid #d5dfe7",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      fontSize: "0.875rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6c757d",
      fontSize: "0.875rem",
      fontWeight: "400",
      margin: 0,
      padding: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#000",
      fontSize: "0.875rem",
      fontWeight: "500",
      margin: 0,
      padding: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: height,
      paddingRight: "8px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#1eaae7" : "transparent",
      color: state.isSelected ? "#fff" : "#333",
      cursor: "pointer",
      "&:hover, &:focus, &:active": {
        backgroundColor: state.isSelected ? "#1eaae7" : "transparent",
        color: state.isSelected ? "#fff" : "#333",
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "10px",
      overflow: "hidden",
      zIndex: 9999,
    }),
  };

  return (
    <div className={wrapClass}>
      {label && (
        <label className={`text-black fw-bold mb-1 ${labelClass}`}>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      <Select
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isDisabled}
        styles={customStyles}
      />
      {error && <div className="text-danger fs-12 mt-1 ms-3">{error}</div>}
    </div>
  );
};

export default CommonSelect;
